/**
 * Moderator Route Protection Component
 * Protects moderator routes and checks user permissions
 */

import { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useTokenGuard } from './use-token-guard';
import { canAccessModeratorWorkspace, homePathForRole, isAdminRole } from '@/lib/auth/roles';

interface ModeratorRouteProps {
  children: ReactNode;
}

export function ModeratorRoute({ children }: ModeratorRouteProps) {
  const { user } = useApp();
  const { isValid } = useTokenGuard();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';

  if (!isValid || !user) {
    return (
      <Navigate
        to={`/${currentLang}/login`}
        state={{ from: window.location.pathname }}
        replace
      />
    );
  }

  if (isAdminRole(user.role)) {
    const adminDestination = location.pathname.endsWith('/moderator/get-paid')
      ? `/${currentLang}/admin/test-moderation`
      : location.pathname.endsWith('/moderator/contracts')
        ? `/${currentLang}/admin/contracts`
        : `/${currentLang}/admin/dashboard`;
    return <Navigate to={adminDestination} replace />;
  }

  if (!canAccessModeratorWorkspace(user.role)) {
    return <Navigate to={homePathForRole(user.role, currentLang)} replace />;
  }

  return <>{children}</>;
}
